import React, { useEffect, useState } from 'react';
import { Image, View } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { isPhone, isWeb, Alert, ImageUpload } from '@helpers';
import { scale, notchBottom } from '@components/frame';
import LineButton from '@components/common/lineButton';

import { getBoxShadowStyle } from '@styles/shadow';
import ColorWrapper from '@styles/colors';

import { deleteImage, cropImage } from '@reducers/imageHandling';
import { getBlob, updateUserPicture } from '@reducers/user';

import { KEYS_TAGS, saveImageFN } from '@components/imageHandling/common';

import CropImageMobile from '@v2/components/imageHandling/components/Cropper/components/CropperMobile';
import CropImageTablet from '@v2/components/imageHandling/components/Cropper/components/CropperTablet';
import CropImageWeb from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';
import Loading from '@components/loading';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const EditImage = ({ updateUserPicture, hide, setUploadingImage, cropImage, C, deleteImage, isCropModeInit = false, user: { userInfo = {} } }) => {
  const [isCropMode, setIsCropMode] = useState(isCropModeInit);

  const { userPicture = {}, userProfile = {} } = userInfo;
  const { userId } = userProfile;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  useEffect(() => {
    const loadOriginalUserImageBlob = async () => {
      try {
        if (!userPicture?.originUrl) return;

        const base64Original = await getBlob(userPicture?.originUrl, userPicture?.id);

        const imageInfo = await new Promise((resolve, reject) => {
          Image.getSize(
            base64Original,
            (width, height) => {
              const cropSettings = userPicture.cropSettings || [];
              let crop = {};
              const page = userPicture.page || 0;
              let c = cropSettings.find((s) => s.pageNumber === page);

              if (c) {
                const angle = c.rotationAngle;
                if (angle == 0 || angle == 180) {
                  crop = {
                    width: Math.round(width * (c.lowerRightXPosition - c.upperLeftXPosition)),
                    height: Math.round(height * (c.lowerRightYPosition - c.upperLeftYPosition)),
                    x: Math.round(c.upperLeftXPosition * width),
                    y: Math.round(c.upperLeftYPosition * height),
                  };
                } else {
                  crop = {
                    width: Math.round(height * (c.lowerRightXPosition - c.upperLeftXPosition)),
                    height: Math.round(width * (c.lowerRightYPosition - c.upperLeftYPosition)),
                    x: Math.round(c.upperLeftXPosition * height),
                    y: Math.round(c.upperLeftYPosition * width),
                  };
                }
                crop.angle = angle;
              }

              const data = {
                id: userPicture.id,
                size3x: { width, height },
                showPDFUploading: false,
                isPreview: false,
                ...crop,
              };
              resolve(data);
            },
            (error) => reject(error)
          );
        });

        updateUserPicture({
          ...userPicture,
          base64Original,
          ...imageInfo,
        });
      } catch (error) {
        console.log(error);
      }
    };

    if (!userPicture?.base64Original) {
      loadOriginalUserImageBlob();
    }
  }, [userPicture?.id, userPicture?.originUrl]);

  if (!userPicture?.base64Original && userPicture?.originUrl) {
    return (
      <View style={{ position: 'absolute', left: '50%', top: '50%', transform: [{ translateX: '-50%' }, { translateY: '-50%' }] }}>
        <Loading background="transparent" />
      </View>
    );
  }

  const deleteUserImage = () =>
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        {
          text: i18n.t('No'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Yes'),
          onPress: async () => {
            const id = userInfo?.userPicture?.id;
            updateUserPicture(null);
            await deleteImage(id);
            hide();
          },
        },
      ],
    });

  const actions = [
    {
      name: 'Delete Photo',
      action: deleteUserImage,
    },
    {
      name: 'Crop Photo',
      action: () => {
        if (userPicture.localImage) {
          hide();
        } else {
          setIsCropMode(true);
        }
      },
    },
    {
      name: 'Replace Photo...',
      action: () => {
        hide();
        ImageUpload({
          headerText: 'Replace User Image',
          withoutAddExisting: true,
          uploadStart: () => setUploadingImage(true),
          uploadEnd: async (img) => {
            const id = userInfo?.userPicture?.id;
            await deleteImage(id);
            setUploadingImage(false);
            updateUserPicture({ ...userPicture, base64: img.uri, localImage: true });
          },
          tags: [
            {
              scopeId: KEYS_TAGS.UserAccount,
              scopeReferenceName: userId,
              isFavorite: true,
            },
          ],
        });
      },
    },
  ];

  if (isCropMode) {
    const data = userPicture;
    const onSave = (data) =>
      saveImageFN(data, {
        images: [userPicture],
        selectedIndex: 0,
        setImages: (images) => {
          updateUserPicture(images[0]);
        },
        cropImage,
        trigerCrop: hide,
      });

    if (isWeb) {
      return (
        <CropImageWeb
          {...{
            onBack: () => {
              hide();
            },
            onDelete: deleteUserImage,
            onSave,
            image: data,
            C,
          }}
        />
      );
    }

    if (isPhone) {
      return (
        <CropImageMobile
          {...{
            triggerShow: () => {
              hide();
            },
            onSave,
            image: data,
            C,
          }}
        />
      );
    } else {
      return (
        <CropImageTablet
          {...{
            triggerShow: () => {
              hide();
            },
            onSave,
            image: data,
            C,
          }}
        />
      );
    }
  }

  return (
    <View
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        zIndex: 100000,
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
      }}
    >
      <View
        style={
          isPhone
            ? {
                width: '100%',
                bottom: 0,
                position: 'absolute',
                paddingBottom: notchBottom + scale(8),
                paddingHorizontal: scale(8),
              }
            : {
                width: scale(330),
              }
        }
      >
        <View
          style={[
            shadowStyles,
            {
              marginBottom: scale(isPhone ? 8 : 0),
              backgroundColor: C('Background / Light / Field'),
              borderRadius: scale(isPhone ? SCREEN_BORDER_RADIUS : 0),
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          {actions.map((a, index) => (
            <LineButton
              key={a.name}
              {...{
                height: 50,
                onPress: () => {
                  a.action();
                },
                text: a.name,
                color: 'Link/Light/Primary',
                C,
                shadow: false,
                borderBottom: isPhone ? index !== actions.length - 1 : true,
                borderRadiusTop: index === 0,
                borderRadiusBottom: isPhone ? index === actions.length - 1 : false,
                font: 'Body Large',
              }}
            />
          ))}
        </View>
        <View
          style={[
            {
              backgroundColor: C('Background / Light / Field'),
              borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
            isPhone && {
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          <LineButton
            {...{ onPress: hide, text: 'Cancel', color: 'Link/Light/Primary', font: 'Headline Large', C, height: 50, borderRadiusTop: isPhone }}
          />
        </View>
      </View>
    </View>
  );
};

export default connect(({ user }) => ({ user }), {
  deleteImage,
  cropImage,
  updateUserPicture,
})(ColorWrapper(EditImage));
